import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import { Pane, FileUploader, FileCard } from "evergreen-ui";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import useGetExternalDocsSummary from "../../hooks/useGetExternalDocsSummary";
import useCreateExternalDocsMutation from "../../hooks/useCreateExternalDocsMutation";
import Loader from "../../components/Loader";

const PatientExternalDocuments = () => {
  const { _id_ } = useParams();
  const navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem("user_data"));
  const role = userData?.role;

  const { trialFilter, setActive, setBreadcrumbTitle, isTrialLocked } = useOutletContext();

  setActive("documents");
  setBreadcrumbTitle("External Documents");

  const { data: summaryData, isLoading } = useGetExternalDocsSummary({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const createMutation = useCreateExternalDocsMutation();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState("");
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]);
  const [fileRejections, setFileRejections] = useState([]);

  const allowedRoles = [
    "CRO Admin",
    "Investigator",
    "Sub_investigator",
    "Cro_coordinator",
    "Data Manager",
    "Inv_coordinator",
    "Super Admin",
    "Clinic Owner",
    "Clinic Admin",
  ];

  const canUpload = allowedRoles.includes(role);

  const routeTo = (visitId, visitName) => {
    const target = visitId
      ? `/patient/${_id_}/documents/${visitId}?trial=${trialFilter}`
      : `/patient/${_id_}/documents/no-visit?trial=${trialFilter}`;
    navigate(target, { state: { visitName, trialId: trialFilter } });
  };

  const handleFileChange = (newFiles) => {
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleFileRejected = (rejections) => {
    setFileRejections([...rejections]);
  };

  const handleFileRemove = (fileToRemove) => {
    setFiles(files.filter((file) => file !== fileToRemove));
    setFileRejections(fileRejections.filter((rejection) => rejection.file !== fileToRemove));
  };

  const handleUpload = () => {
    if (!files || files.length === 0) return;

    createMutation.mutate(
      {
        patientId: _id_,
        trialId: trialFilter || "",
        visitId: selectedVisit || "",
        comment,
        files,
      },
      {
        onSuccess: () => {
          setFiles([]);
          setComment("");
          setSelectedVisit("");
          setFileRejections([]);
          setShowUploadModal(false);
        },
      }
    );
  };

  const handleCloseUploadModal = () => {
    if (createMutation.isPending) return;
    setShowUploadModal(false);
    setFiles([]);
    setComment("");
    setSelectedVisit("");
    setFileRejections([]);
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = { whiteSpace: "nowrap" };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">External Documents</Typography>

        {canUpload && (
          <Button
            size="small"
            variant="contained"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => setShowUploadModal(true)}
            disabled={isTrialLocked}
          >
            Upload Documents
          </Button>
        )}
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Visit Name</TableCell>
              <TableCell sx={headerCellSx}>Documents Count</TableCell>
              <TableCell sx={headerCellSx}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              summaryData?.visits?.map((visit) => (
                <TableRow key={visit.visit_id} hover>
                  <TableCell sx={bodyCellSx}>{visit.visit_name}</TableCell>
                  <TableCell sx={bodyCellSx}>{visit.documents_count}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => routeTo(visit.visit_id, visit.visit_name)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

            {/* No Visit row */}
            {!isLoading && summaryData?.no_visit && (
              <TableRow hover>
                <TableCell sx={bodyCellSx}>{summaryData.no_visit.visit_name}</TableCell>
                <TableCell sx={bodyCellSx}>{summaryData.no_visit.documents_count}</TableCell>
                <TableCell sx={bodyCellSx}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => routeTo(null, summaryData.no_visit.visit_name)}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !summaryData?.visits?.length && !summaryData?.no_visit && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No documents found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onClose={handleCloseUploadModal} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Documents</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Visit (optional)</InputLabel>
              <Select
                label="Visit (optional)"
                value={selectedVisit}
                onChange={(e) => setSelectedVisit(e.target.value)}
              >
                <MenuItem value="">
                  <em>No Visit</em>
                </MenuItem>
                {summaryData?.available_visits?.map((visit) => (
                  <MenuItem key={visit.visit_id} value={visit.visit_id}>
                    {visit.visit_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Pane>
              <FileUploader
                label="Upload Files"
                description="You can upload multiple files. Each file can be up to 50 MB."
                maxSizeInBytes={50 * 1024 ** 2}
                onChange={handleFileChange}
                onRejected={handleFileRejected}
                renderFile={(file) => {
                  const { name, size, type } = file;
                  const rejection = fileRejections.find((r) => r.file === file);
                  const { message } = rejection || {};
                  return (
                    <FileCard
                      key={name}
                      isInvalid={rejection !== null}
                      name={name}
                      onRemove={() => handleFileRemove(file)}
                      sizeInBytes={size}
                      type={type}
                      validationMessage={message}
                    />
                  );
                }}
                values={files}
              />
            </Pane>

            <TextField
              label="Comment"
              name="comment"
              multiline
              rows={4}
              size="small"
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseUploadModal} disabled={createMutation.isPending}>
            Cancel
          </Button>

          <Box sx={{ position: "relative" }}>
            <Button
              variant="contained"
              startIcon={<CloudUploadOutlinedIcon />}
              onClick={handleUpload}
              disabled={createMutation.isPending || !files || files.length === 0}
            >
              {createMutation.isPending ? "Uploading..." : "Upload"}
            </Button>
            {createMutation.isPending && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PatientExternalDocuments;
